import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Rating,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddCustomerDialog from "../components/AddCustomerDialog";
import MultiSelectAutocomplete from "../components/MultiSelectAutocomplete";
import { authAPI } from "../services/api";
import {
  addCustomer,
  loadAllReferenceData,
  selectBranches,
  selectCommunicationMethods,
  selectContactReasons,
  selectCustomers,
  selectDepartments,
  selectIsReferenceDataLoading,
  selectProducts,
  selectReferenceDataError,
  selectShortSubjects,
} from "../store/slices/referenceDataSlice";
import { createTicket } from "../store/slices/ticketsSlice";
import { useRequirePermission } from "../hooks/useHasPermission";
import { PERMISSIONS } from "../utils/permissions";

const CreateTicketPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const hasAccess = useRequirePermission(PERMISSIONS.CREATE_TICKETS, onNavigate);
  const { error } = useSelector((state) => state.tickets);
  const { user } = useSelector((state) => state.auth);

  // Get reference data from Redux store
  const communicationMethods = useSelector(selectCommunicationMethods);
  const contactReasons = useSelector(selectContactReasons);
  const shortSubjects = useSelector(selectShortSubjects);
  const branches = useSelector(selectBranches);
  const products = useSelector(selectProducts);
  const departments = useSelector(selectDepartments);
  const customers = useSelector(selectCustomers);
  const isReferenceDataLoading = useSelector(selectIsReferenceDataLoading);
  const referenceDataError = useSelector(selectReferenceDataError);
  const isRTL = i18n.language === "ar";

  const titleOptions = useMemo(
    () => shortSubjects.map((s) => (isRTL ? s.name_ar : s.name_en)),
    [shortSubjects, isRTL]
  );

  const getReferenceLabel = (item) =>
    (isRTL ? item.name_ar : item.name_en) || item.name_ar;

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    customer: "",
    communication_method: "",
    contact_reason: "",
    branches: [],
    products: [],
    departments: [],
    customer_rating: null,
    priority: "medium",
  });

  // Form validation
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add customer dialog state
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);

  // Customer search state
  const [customerSearchValue, setCustomerSearchValue] = useState("");

  // Attachments state
  const [attachments, setAttachments] = useState([]);

  // Initialize customer search value when form data changes
  useEffect(() => {
    if (formData.customer && customers.length > 0) {
      const selectedCustomer = customers.find(
        (c) => c.customer_id.toString() === formData.customer
      );
      if (selectedCustomer) {
        setCustomerSearchValue(selectedCustomer.name);
      }
    }
  }, [formData.customer, customers]);

  // Load reference data if not already loaded
  useEffect(() => {
    if (
      customers.length === 0 &&
      !isReferenceDataLoading &&
      !referenceDataError
    ) {
      dispatch(loadAllReferenceData());
    }
  }, [customers.length, isReferenceDataLoading, referenceDataError, dispatch]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field] || errors.general) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
        general: "",
      }));
    }
  };

  const handleDepartmentChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      departments: typeof value === "string" ? value.split(",") : value,
    }));
  };

  // Customer search function
  const handleCustomerSearch = (event, searchValue) => {
    setCustomerSearchValue(searchValue);
    // لا نحتاج لفلترة منفصلة، Autocomplete سيتولى ذلك
  };

  // Customer selection handler
  const handleCustomerSelect = (event, selectedCustomer) => {
    if (selectedCustomer) {
      setFormData((prev) => ({
        ...prev,
        customer: selectedCustomer.customer_id.toString(),
      }));
      setCustomerSearchValue(selectedCustomer.name);
    } else {
      setFormData((prev) => ({
        ...prev,
        customer: "",
      }));
      setCustomerSearchValue("");
    }

    // Clear error when user selects a customer
    if (errors.customer || errors.general) {
      setErrors((prev) => ({
        ...prev,
        customer: "",
        general: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t("createTicket.titleRequired");
    }

    if (!formData.message.trim()) {
      newErrors.message = t("createTicket.messageRequired");
    }

    if (!formData.communication_method) {
      newErrors.communication_method = t(
        "createTicket.communicationMethodRequired"
      );
    }

    if (!formData.contact_reason) {
      newErrors.contact_reason = t("createTicket.contactReasonRequired");
    }

    if (formData.departments.length === 0) {
      newErrors.departments = t("createTicket.departmentsRequired");
    }

    if (!formData.priority) {
      newErrors.priority = "يجب تحديد أهمية التذكرة";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check if user is authenticated
    if (!user) {
      console.error("User not authenticated");
      setErrors({ general: "يجب تسجيل الدخول أولاً" });
      return;
    }

    // Check if reference data is loaded
    if (isReferenceDataLoading) {
      setErrors({ general: "جاري تحميل البيانات المرجعية، يرجى الانتظار..." });
      return;
    }

    if (referenceDataError) {
      setErrors({
        general:
          "لا يمكن إنشاء التذكرة: فشل في تحميل البيانات المرجعية من قاعدة البيانات",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // First, verify authentication by checking the profile
      try {
        await authAPI.checkAuth();
      } catch (authError) {
        console.error("Authentication check failed:", authError);
        setErrors({
          general: "انتهت جلسة المستخدم. يرجى تسجيل الدخول مرة أخرى",
        });
        return;
      }

      const ticketData = {
        ...formData,
        departments: formData.departments.map((deptId) => parseInt(deptId)),
        branches: formData.branches.map((branchId) => parseInt(branchId)),
        products: formData.products.map((productId) => parseInt(productId)),
        customer: formData.customer ? parseInt(formData.customer) : null,
        communication_method: parseInt(formData.communication_method),
        contact_reason: parseInt(formData.contact_reason),
      };

      let payload = ticketData;
      if (attachments.length > 0) {
        const formDataPayload = new FormData();
        formDataPayload.append("title", ticketData.title);
        formDataPayload.append("message", ticketData.message);
        if (ticketData.customer) {
          formDataPayload.append("customer", ticketData.customer);
        }
        formDataPayload.append(
          "communication_method",
          ticketData.communication_method
        );
        formDataPayload.append("contact_reason", ticketData.contact_reason);
        formDataPayload.append(
          "departments",
          JSON.stringify(ticketData.departments)
        );
        formDataPayload.append("branches", JSON.stringify(ticketData.branches));
        formDataPayload.append("products", JSON.stringify(ticketData.products));
        if (ticketData.customer_rating != null) {
          formDataPayload.append("customer_rating", ticketData.customer_rating);
        }
        formDataPayload.append("priority", ticketData.priority);
        attachments.forEach((attachment) => {
          formDataPayload.append("attachments", attachment.file);
        });
        payload = formDataPayload;
      }

      await dispatch(createTicket(payload)).unwrap();

      // Reset form
      setFormData({
        title: "",
        message: "",
        customer: "",
        communication_method: "",
        contact_reason: "",
        branches: [],
        products: [],
        departments: [],
        customer_rating: null,
        priority: "medium",
      });
      setAttachments([]);

      // Navigate to home
      if (onNavigate) {
        onNavigate("/home");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);

      // تحسين معالجة الأخطاء
      let errorMessage = "حدث خطأ في إنشاء التذكرة";

      if (error.message) {
        // معالجة أخطاء مختلفة
        if (error.message.includes("400")) {
          errorMessage =
            "البيانات المدخلة غير صحيحة. يرجى التحقق من جميع الحقول المطلوبة";
        } else if (error.message.includes("401")) {
          errorMessage = "انتهت جلسة المستخدم. يرجى تسجيل الدخول مرة أخرى";
        } else if (error.message.includes("403")) {
          errorMessage = "ليس لديك صلاحية لإنشاء تذاكر جديدة";
        } else if (error.message.includes("500")) {
          errorMessage = "خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً";
        } else if (
          error.message.includes("Network Error") ||
          error.message.includes("Failed to fetch")
        ) {
          errorMessage = "خطأ في الاتصال بالخادم. تأكد من اتصال الإنترنت";
        } else {
          errorMessage = error.message;
        }
      }

      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate("/home");
    }
  };

  const handleAddCustomer = () => {
    setAddCustomerDialogOpen(true);
  };

  // Handle file attachments
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const handleRemoveAttachment = (attachmentId) => {
    setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
  };

  const handleCustomerAdded = (newCustomer) => {
    // Add the new customer to Redux store
    dispatch(addCustomer(newCustomer));

    // Update the customer field with the new customer
    setFormData((prev) => ({
      ...prev,
      customer: newCustomer.customer_id.toString(),
    }));

    // Update the search value to show the new customer name
    setCustomerSearchValue(newCustomer.name);

    // Clear any customer-related errors
    if (errors.customer) {
      setErrors((prev) => ({
        ...prev,
        customer: "",
      }));
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <Box
      sx={{
        p: { xs: 0.5, sm: 1, md: 1.5 },
        maxWidth: "100%",
        width: "100%",
      }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
            mb: 2,
            fontWeight: "bold",
          }}
        >
          {t("createTicket.title")}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isReferenceDataLoading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            جاري تحميل البيانات من قاعدة البيانات...
          </Alert>
        )}

        {referenceDataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            خطأ: فشل في تحميل البيانات المرجعية من قاعدة البيانات. تأكد من أن
            الخادم يعمل وأن قاعدة البيانات متصلة.
          </Alert>
        )}

        {!isReferenceDataLoading &&
          !referenceDataError &&
          (communicationMethods.length === 0 ||
            contactReasons.length === 0 ||
            departments.length === 0 ||
            customers.length === 0) && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              تحذير: لا توجد بيانات مرجعية متاحة. تأكد من وجود بيانات في قاعدة
              البيانات للأقسام وطرق التواصل وأسباب التواصل والعملاء.
            </Alert>
          )}

        {errors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.general}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ width: "100%" }}>
            {/* Title Field */}
            <Box sx={{ mb: 3, width: "100%" }}>
              <Autocomplete
                freeSolo
                fullWidth
                options={titleOptions}
                value={formData.title}
                onInputChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, title: newValue || "" }));
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }
                }}
                onChange={(event, newValue) => {
                  const title =
                    typeof newValue === "string" ? newValue : newValue || "";
                  setFormData((prev) => ({ ...prev, title }));
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }
                }}
                disabled={isReferenceDataLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("createTicket.titleLabel")}
                    error={!!errors.title}
                    helperText={errors.title}
                    required
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: { xs: "16px", sm: "16px" },
                        minHeight: { xs: "56px", sm: "56px" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "16px", sm: "16px" },
                      },
                    }}
                  />
                )}
              />
            </Box>

            {/* Customer Field with Add Button and Rating */}
            <Box sx={{ mb: 3, width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                {/* Customer Selection with Add Button */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Autocomplete
                    fullWidth
                    options={customers}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.name} - ${option.contact}`;
                    }}
                    value={
                      customers.find(
                        (c) => c.customer_id.toString() === formData.customer
                      ) || null
                    }
                    onChange={handleCustomerSelect}
                    onInputChange={handleCustomerSearch}
                    inputValue={customerSearchValue}
                    disabled={isReferenceDataLoading}
                    noOptionsText="لا توجد نتائج"
                    loadingText="جاري البحث..."
                    freeSolo={false}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return option.customer_id === value.customer_id;
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue || inputValue.length < 1) {
                        return options.slice(0, 50); // عرض أول 50 عميل
                      }
                      const filter = inputValue.toLowerCase();
                      const filtered = options.filter(
                        (option) =>
                          option.name.toLowerCase().includes(filter) ||
                          option.contact.toLowerCase().includes(filter) ||
                          (option.email &&
                            option.email.toLowerCase().includes(filter))
                      );
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("createTicket.customerLabel")}
                        placeholder="ابحث بالاسم أو رقم التواصل..."
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: { xs: "16px", sm: "16px" },
                            minHeight: { xs: "56px", sm: "56px" },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: { xs: "16px", sm: "16px" },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.contact}
                            {option.email && ` • ${option.email}`}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: "300px", // تحديد ارتفاع أقصى للقائمة
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleAddCustomer}
                    disabled={isReferenceDataLoading}
                    color="primary"
                    sx={{
                      mt: 1,
                      height: "56px",
                      width: "56px",
                      border: "1px solid",
                      borderColor: "primary.main",
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "white",
                      },
                    }}
                    title="إضافة عميل جديد"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {/* Customer Rating */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    minWidth: { xs: "100%", md: "300px" },
                    height: "56px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "14px", sm: "16px" },
                      fontWeight: 500,
                      minWidth: "fit-content",
                    }}
                  >
                    {t("createTicket.customerRatingLabel")}:
                  </Typography>
                  <Rating
                    value={formData.customer_rating}
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        customer_rating: newValue,
                      }));
                    }}
                    size="medium"
                    sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem" } }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Four Fields - Responsive Flexbox Layout */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 2, lg: 2 },
                mb: 3,
                width: "100%",
              }}
            >
              {/* Communication Method */}
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <FormControl
                  fullWidth
                  error={!!errors.communication_method}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                      minHeight: { xs: "56px", sm: "56px" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                    },
                  }}
                >
                  <InputLabel>
                    {t("createTicket.communicationMethodLabel")}
                  </InputLabel>
                  <Select
                    value={formData.communication_method}
                    onChange={handleInputChange("communication_method")}
                    label={t("createTicket.communicationMethodLabel")}
                    required
                    disabled={isReferenceDataLoading}
                  >
                    {communicationMethods.map((method) => (
                      <MenuItem key={method.method_id} value={method.method_id}>
                        {method.name_ar}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.communication_method && (
                    <FormHelperText>
                      {errors.communication_method}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Contact Reason */}
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <FormControl
                  fullWidth
                  error={!!errors.contact_reason}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                      minHeight: { xs: "56px", sm: "56px" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                    },
                  }}
                >
                  <InputLabel>
                    {t("createTicket.contactReasonLabel")}
                  </InputLabel>
                  <Select
                    value={formData.contact_reason}
                    onChange={handleInputChange("contact_reason")}
                    label={t("createTicket.contactReasonLabel")}
                    required
                    disabled={isReferenceDataLoading}
                  >
                    {contactReasons.map((reason) => (
                      <MenuItem key={reason.reason_id} value={reason.reason_id}>
                        {reason.name_ar}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.contact_reason && (
                    <FormHelperText>{errors.contact_reason}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Branch */}
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <MultiSelectAutocomplete
                  label={t("createTicket.branchLabel")}
                  placeholder={t("createTicket.selectBranch")}
                  options={branches}
                  value={formData.branches}
                  onChange={(branchIds) =>
                    setFormData((prev) => ({ ...prev, branches: branchIds }))
                  }
                  getOptionLabel={getReferenceLabel}
                  getOptionValue={(branch) => branch.branch_id}
                  disabled={isReferenceDataLoading}
                  isRTL={isRTL}
                />
              </Box>

              {/* Products */}
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <MultiSelectAutocomplete
                  label={t("createTicket.productLabel")}
                  placeholder={t("createTicket.selectProduct")}
                  options={products}
                  value={formData.products}
                  onChange={(productIds) =>
                    setFormData((prev) => ({ ...prev, products: productIds }))
                  }
                  getOptionLabel={getReferenceLabel}
                  getOptionValue={(product) => product.product_id}
                  disabled={isReferenceDataLoading}
                  isRTL={isRTL}
                />
              </Box>
            </Box>

            {/* Departments and Priority Fields - Same Row */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 2, lg: 2 },
                mb: 3,
                width: "100%",
              }}
            >
              {/* Departments Field - 8/12 */}
              <Box sx={{ flex: { xs: "1 1 100%", lg: "8" } }}>
                <FormControl
                  fullWidth
                  error={!!errors.departments}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                      minHeight: { xs: "56px", sm: "56px" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                    },
                  }}
                >
                  <InputLabel>{t("createTicket.departmentsLabel")}</InputLabel>
                  <Select
                    multiple
                    value={formData.departments}
                    onChange={handleDepartmentChange}
                    input={
                      <OutlinedInput
                        label={t("createTicket.departmentsLabel")}
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const dept = departments.find(
                            (d) => d.department_id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={dept ? dept.name_ar : value}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                    required
                    disabled={isReferenceDataLoading}
                  >
                    {departments.map((department) => (
                      <MenuItem
                        key={department.department_id}
                        value={department.department_id}
                      >
                        {department.name_ar}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.departments && (
                    <FormHelperText>{errors.departments}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Priority Field - 4/12 */}
              <Box sx={{ flex: { xs: "1 1 100%", lg: "4" } }}>
                <FormControl
                  fullWidth
                  error={!!errors.priority}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                      minHeight: { xs: "56px", sm: "56px" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "16px", sm: "16px" },
                    },
                  }}
                >
                  <InputLabel>أهمية التذكرة</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={handleInputChange("priority")}
                    label="أهمية التذكرة"
                    required
                  >
                    <MenuItem value="low">منخفضة</MenuItem>
                    <MenuItem value="medium">متوسطة</MenuItem>
                    <MenuItem value="high">عالية</MenuItem>
                    <MenuItem value="urgent">عاجلة</MenuItem>
                  </Select>
                  {errors.priority && (
                    <FormHelperText>{errors.priority}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Message Field - Moved to end with more rows */}
            <Box sx={{ mb: 3, width: "100%" }}>
              <TextField
                fullWidth
                multiline
                rows={12}
                label={t("createTicket.messageLabel")}
                value={formData.message}
                onChange={handleInputChange("message")}
                error={!!errors.message}
                helperText={errors.message}
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: "16px", sm: "16px" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "16px", sm: "16px" },
                  },
                }}
              />
            </Box>

            {/* Attachments Field */}
            <Box sx={{ mb: 3, width: "100%" }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                المرفقات
              </Typography>

              {/* File Upload Button */}
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
                  style={{ display: "none" }}
                  id="file-upload"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddIcon />}
                    sx={{
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      height: "56px",
                      fontSize: { xs: "14px", sm: "16px" },
                    }}
                  >
                    إضافة مرفقات (صور، فيديو، ملفات)
                  </Button>
                </label>
              </Box>

              {/* Attachments List */}
              {attachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    المرفقات المحددة ({attachments.length}):
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {attachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        label={`${attachment.name} (${(
                          attachment.size / 1024
                        ).toFixed(1)} KB)`}
                        onDelete={() => handleRemoveAttachment(attachment.id)}
                        color="primary"
                        variant="outlined"
                        sx={{
                          maxWidth: "200px",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", sm: "flex-end" },
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                size="large"
                fullWidth={{ xs: true, sm: false }}
                sx={{
                  minWidth: { xs: "100%", sm: "140px" },
                  height: { xs: "48px", sm: "48px" },
                  fontSize: { xs: "16px", sm: "16px" },
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                size="large"
                fullWidth={{ xs: true, sm: false }}
                sx={{
                  minWidth: { xs: "100%", sm: "180px" },
                  height: { xs: "48px", sm: "48px" },
                  fontSize: { xs: "16px", sm: "16px" },
                }}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting
                  ? t("createTicket.creating")
                  : t("createTicket.create")}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onCustomerAdded={handleCustomerAdded}
      />
    </Box>
  );
};

export default CreateTicketPage;
